// ============================================
// FAILURE PAGE
// ============================================

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XCircle, RefreshCw, MessageCircle, Shield } from 'lucide-react';
import { Layout } from '../../components/layout';
import { Card, CardContent, Button, Alert } from '../../components/ui';
import { ROUTES, SUPPORT_EMAIL } from '../../constants';
import styles from './ResultPages.module.css';

export function FailurePage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const handleRetry = () => {
    navigate(ROUTES.FORM);
  };

  const handleContactSupport = () => {
    const subject = encodeURIComponent('Payment Issue - Device Recommendation');
    const body = encodeURIComponent(`Hi FL Smartech Team,\n\nI'm having trouble completing my payment for the device recommendation service.\n\nIssue details:\n\n`);
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${SUPPORT_EMAIL}&su=${subject}&body=${body}`, '_blank');
  };

  return (
    <Layout maxWidth="sm">
      <div className={styles.container}>
        {/* Failure Icon */}
        <div className={styles.iconWrapper + ' ' + styles.failure}>
          <XCircle size={48} strokeWidth={1.5} />
        </div>

        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>Payment Not Completed</h1>
          <p className={styles.subtitle}>
            Don't worry — your information is saved and no amount has been charged.
          </p>
        </div>

        {/* Info Card */}
        <Card variant="elevated">
          <CardContent>
            <Alert variant="info" title="What happened?">
              The payment was either cancelled or could not be processed. 
              This can happen due to network issues, bank declines, or if you closed the payment window.
            </Alert>

            <div className={styles.actionButtons}>
              <Button onClick={handleRetry} size="lg" fullWidth>
                <RefreshCw size={18} />
                Try Again
              </Button>
              <Button variant="secondary" fullWidth onClick={handleContactSupport}>
                <MessageCircle size={18} />
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reassurance */}
        <Card variant="bordered" padding="md">
          <CardContent>
            <div className={styles.infoContent}>
              <Shield size={24} className={styles.infoIconGreen} />
              <div>
                <h3 className={styles.infoTitle}>Your data is safe</h3>
                <p className={styles.infoText}>
                  All the information you provided has been securely saved. 
                  When you retry, you won't need to fill the form again.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Common Issues */}
        <div className={styles.helpSection}>
          <h3 className={styles.helpTitle}>Common issues</h3>
          <ul className={styles.helpList}>
            <li>Ensure you have sufficient balance in your account</li>
            <li>Check if international transactions are enabled</li>
            <li>Try a different payment method (UPI, Card, Net Banking)</li>
            <li>Disable any popup blockers in your browser</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}
